import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const LangSwitcher = ({ className = "", compact = false }) => {
  const { lang, setLang } = useLanguage();
  const btn = (code) =>
    `transition-colors duration-300 ${lang === code ? "text-[#F97316]" : "text-white/40 hover:text-white"}`;
  return (
    <div
      className={`flex items-center gap-2.5 font-spacemono uppercase tracking-[0.2em] ${compact ? "text-sm" : "text-[11px]"} ${className}`}
      data-testid="language-switcher"
    >
      <button onClick={() => setLang("id")} className={btn("id")} data-testid="lang-id-button" aria-label="Bahasa Indonesia">
        ID
      </button>
      <span className="text-white/20">|</span>
      <button onClick={() => setLang("en")} className={btn("en")} data-testid="lang-en-button" aria-label="English">
        EN
      </button>
    </div>
  );
};

export default function Header() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [entered, setEntered] = useState(false);
  const [active, setActive] = useState("");
  const { scrollY, scrollYProgress } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();

  const LINKS = [
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.services"), href: "#services" },
    { label: t("nav.work"), href: "#work" },
    { label: t("nav.team"), href: "#leadership" },
  ];

  const go = (e, href) => {
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate(`/${href}`);
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(y > prev && y > 320);
  });

  useEffect(() => {
    const ids = ["about", "services", "work", "leadership"];
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-35% 0px -60% 0px" }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: hidden ? "-110%" : 0, opacity: 1 }}
        transition={{ duration: entered ? 0.5 : 0.9, delay: entered ? 0 : 1.6, ease: EASE }}
        onAnimationComplete={() => setEntered(true)}
        className={`fixed inset-x-0 top-0 z-[65] transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "border-b border-white/10 bg-black/60 backdrop-blur-xl" : "border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 md:px-12">
          <a href="#top" className="group flex items-center" data-testid="header-logo">
            <img src="/assets/logo-white.png" alt="Payung Oren Creative" className="h-7 w-auto md:h-8" />
          </a>

          <nav className="hidden items-center gap-10 lg:flex" data-testid="header-nav">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className={`group relative font-spacemono text-[11px] uppercase tracking-[0.3em] transition-colors duration-300 hover:text-white ${
                  active === l.href.slice(1) ? "text-white" : "text-white/60"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-[#F97316] transition-[width] duration-300 ${
                    active === l.href.slice(1) ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </a>
            ))}
            <LangSwitcher />
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="nav-cta-contact"
              className="group flex items-center gap-2 rounded-full bg-[#F97316] px-6 py-2.5 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:bg-[#EA580C] active:scale-[0.96]"
            >
              {t("nav.contact")}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>

          <div className="flex items-center gap-5 lg:hidden">
            <LangSwitcher />
            <button
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open-button"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        <motion.div
          className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-[#F97316]"
          style={{ scaleX: scrollYProgress }}
          aria-hidden="true"
        />
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#050505] px-6 py-6"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <img src="/assets/logo-white.png" alt="Payung Oren Creative" className="h-8 w-auto" />
              <button
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-2">
              {[...LINKS, { label: t("nav.contact"), href: "#contact" }].map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => {
                    setOpen(false);
                    go(e, l.href);
                  }}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  className="border-b border-white/10 py-5 font-syne text-4xl font-bold uppercase tracking-tight text-white/85 transition-colors hover:text-[#F97316]"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <motion.div
              className="mt-auto flex items-center justify-between"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <p className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/35">
                {t("nav.tagline")}
              </p>
              <LangSwitcher compact />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
